import { useCallback, useEffect, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { ROOT_DOMAIN } from "@/lib/vercel-domains"
import {
  addDomainToVercel,
  removeDomainFromVercel,
} from "@/lib/vercel-domains"
import {
  checkSubdomainAvailability,
  validateSubdomain,
} from "@/lib/subdomainRules"
import { supabase } from "@/lib/supabase"
import { queuePublicCachePurge } from "@/lib/publicCache"
import { useAuthStore } from "@/stores/authStore"
import { useOrgStore } from "@/stores/orgStore"
import { PermissionGate } from "@/components/PermissionGate"
import { CustomDomainSettings } from "@/components/dashboard/CustomDomainSettings"
import { LegalIdentityCard } from "@/components/settings/LegalIdentityCard"

export function SettingsPage() {
  const [searchParams] = useSearchParams()
  const stripeParam = searchParams.get("stripe")
  const fetchUserOrgs = useOrgStore((s) => s.fetchUserOrgs)
  const currentOrg = useOrgStore((s) => s.currentOrg)
  const userOrgs = useOrgStore((s) => s.userOrgs)
  const user = useAuthStore((s) => s.user)

  const [subdomainEdit, setSubdomainEdit] = useState(false)
  const [draftSubdomain, setDraftSubdomain] = useState("")
  const [formatError, setFormatError] = useState<string | null>(null)
  const [availabilityChecked, setAvailabilityChecked] = useState(false)
  const [availabilityAvailable, setAvailabilityAvailable] = useState<
    boolean | null
  >(null)
  const [checkingAvailability, setCheckingAvailability] = useState(false)
  const [savingSubdomain, setSavingSubdomain] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingNewSubdomain, setPendingNewSubdomain] = useState<string | null>(
    null
  )

  useEffect(() => {
    fetchUserOrgs()
  }, [fetchUserOrgs])

  useEffect(() => {
    if (stripeParam === "success" || stripeParam === "refresh") {
      fetchUserOrgs()
    }
  }, [stripeParam, fetchUserOrgs])

  const org = currentOrg ?? userOrgs[0]
  const currentSlug = org?.slug ?? ""

  const handleSubdomainInputChange = useCallback((value: string) => {
    const normalized = value.trim().toLowerCase()
    setDraftSubdomain(normalized)
    setAvailabilityChecked(false)
    setAvailabilityAvailable(null)
    const result = validateSubdomain(normalized)
    setFormatError(result.valid ? null : result.error ?? null)
  }, [])

  const handleCheckAvailability = useCallback(async () => {
    if (!org?.id) return
    const normalized = draftSubdomain.trim().toLowerCase()
    const formatResult = validateSubdomain(normalized)
    if (!formatResult.valid) {
      setFormatError(formatResult.error ?? null)
      setAvailabilityChecked(true)
      setAvailabilityAvailable(false)
      return
    }
    setCheckingAvailability(true)
    setFormatError(null)
    try {
      const available = await checkSubdomainAvailability(normalized, org.id)
      setAvailabilityChecked(true)
      setAvailabilityAvailable(available)
      if (!available) setFormatError("This subdomain is already taken")
    } catch {
      setFormatError("Could not check availability")
      setAvailabilityAvailable(false)
    } finally {
      setCheckingAvailability(false)
    }
  }, [draftSubdomain, org?.id])

  const canSaveSubdomain =
    draftSubdomain.trim().toLowerCase() !== currentSlug &&
    validateSubdomain(draftSubdomain.trim().toLowerCase()).valid &&
    (availabilityChecked ? availabilityAvailable === true : false)

  const openConfirm = useCallback(() => {
    if (!canSaveSubdomain) return
    setPendingNewSubdomain(draftSubdomain.trim().toLowerCase())
    setConfirmOpen(true)
  }, [canSaveSubdomain, draftSubdomain])

  const saveSubdomain = useCallback(async () => {
    const newSlug = pendingNewSubdomain
    if (!newSlug || !org?.id || !user?.id) return
    setSavingSubdomain(true)
    try {
      if (currentSlug) {
        const removeResult = await removeDomainFromVercel(currentSlug, org.id)
        if (!removeResult.success && removeResult.error) {
          toast.error(removeResult.error)
          setSavingSubdomain(false)
          setConfirmOpen(false)
          setPendingNewSubdomain(null)
          return
        }
      }
      const addResult = await addDomainToVercel(newSlug, org.id)
      if (!addResult.success) {
        toast.error(addResult.error ?? "Failed to add domain to Vercel")
        setSavingSubdomain(false)
        setConfirmOpen(false)
        setPendingNewSubdomain(null)
        return
      }
      const { error: updateError } = await supabase
        .from("organizations")
        .update({ slug: newSlug, updated_at: new Date().toISOString() })
        .eq("id", org.id)
      if (updateError) {
        toast.error(updateError.message)
        setSavingSubdomain(false)
        setConfirmOpen(false)
        setPendingNewSubdomain(null)
        return
      }
      await supabase.from("subdomain_history").insert({
        organization_id: org.id,
        old_subdomain: currentSlug || null,
        new_subdomain: newSlug,
        changed_by: user.id,
      })
      queuePublicCachePurge(org.id, { type: "domain-change" })
      await fetchUserOrgs()
      setSubdomainEdit(false)
      setDraftSubdomain("")
      setFormatError(null)
      setAvailabilityChecked(false)
      setAvailabilityAvailable(null)
      setConfirmOpen(false)
      setPendingNewSubdomain(null)
      toast.success(
        `Subdomain updated. Your website is now at ${newSlug}.${ROOT_DOMAIN}`
      )
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update subdomain")
    } finally {
      setSavingSubdomain(false)
    }
  }, [pendingNewSubdomain, org?.id, org, user?.id, currentSlug, fetchUserOrgs])

  const startEdit = useCallback(() => {
    setDraftSubdomain(currentSlug)
    setFormatError(null)
    setAvailabilityChecked(false)
    setAvailabilityAvailable(null)
    setSubdomainEdit(true)
  }, [currentSlug])

  const cancelEdit = useCallback(() => {
    setSubdomainEdit(false)
    setDraftSubdomain("")
    setFormatError(null)
    setAvailabilityChecked(false)
    setAvailabilityAvailable(null)
  }, [])

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b px-4 py-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-charity-forest">Settings</h1>
          <Button variant="outline" size="sm" asChild>
            <Link to="/dashboard">Back to dashboard</Link>
          </Button>
        </div>
      </header>

      <main className="p-4 sm:p-6">
        {stripeParam === "success" && (
          <Card className="mb-6 border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950">
            <CardHeader>
              <CardTitle className="text-green-800 dark:text-green-200">
                Stripe connected
              </CardTitle>
              <CardDescription className="text-green-700 dark:text-green-300">
                Your organization can now accept donations online.
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        {stripeParam === "refresh" && (
          <Card className="mb-6 border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950">
            <CardHeader>
              <CardTitle className="text-amber-800 dark:text-amber-200">
                Complete onboarding
              </CardTitle>
              <CardDescription className="text-amber-700 dark:text-amber-300">
                Please complete the Stripe Express onboarding to start accepting
                donations. You can try again from the dashboard.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild>
                <Link to="/dashboard">Go to dashboard</Link>
              </Button>
            </CardContent>
          </Card>
        )}

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Organization</CardTitle>
            <CardDescription>
              {org?.name ?? "Loading..."} — {currentSlug || "—"}.{ROOT_DOMAIN}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              More settings (branding, team, billing) will be available in a
              future update.
            </p>
            <Button variant="outline" size="sm" asChild>
              <Link to="/dashboard/settings/ai-profile">Edit AI Profile</Link>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Website Subdomain</CardTitle>
            <CardDescription>
              Your public website URL is{" "}
              {currentSlug ? (
                <span className="font-medium text-foreground">
                  {currentSlug}.{ROOT_DOMAIN}
                </span>
              ) : (
                "not set"
              )}
              . Change it below if needed.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!subdomainEdit ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {currentSlug ? `${currentSlug}.${ROOT_DOMAIN}` : "No subdomain set"}
                </span>
                <Button variant="outline" size="sm" onClick={startEdit}>
                  Edit
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="subdomain-input">Subdomain</Label>
                  <div className="flex flex-wrap gap-2">
                    <Input
                      id="subdomain-input"
                      value={draftSubdomain}
                      onChange={(e) =>
                        handleSubdomainInputChange(e.target.value)
                      }
                      onBlur={() => {
                        if (
                          draftSubdomain.trim() &&
                          validateSubdomain(draftSubdomain.trim().toLowerCase()).valid
                        ) {
                          handleCheckAvailability()
                        }
                      }}
                      placeholder="your-org"
                      className="max-w-xs font-mono"
                    />
                    <span className="flex items-center text-sm text-muted-foreground">
                      .{ROOT_DOMAIN}
                    </span>
                  </div>
                  {formatError && (
                    <p className="text-sm text-destructive">{formatError}</p>
                  )}
                  {availabilityChecked && availabilityAvailable === true && !formatError && (
                    <p className="text-sm text-green-600 dark:text-green-400">
                      This subdomain is available
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCheckAvailability}
                    disabled={checkingAvailability}
                  >
                    {checkingAvailability ? "Checking…" : "Check Availability"}
                  </Button>
                  <Button
                    size="sm"
                    onClick={openConfirm}
                    disabled={!canSaveSubdomain}
                  >
                    Save Subdomain
                  </Button>
                  <Button variant="ghost" size="sm" onClick={cancelEdit}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <PermissionGate permission="can_manage_settings">
          {org?.id && (
            <div className="mt-6">
              <CustomDomainSettings
                orgId={org.id}
                orgSlug={currentSlug || "your-org"}
              />
            </div>
          )}
        </PermissionGate>

        <PermissionGate permission="can_manage_settings">
          {org?.id && (
            <div className="mt-6">
              <LegalIdentityCard orgId={org.id} />
            </div>
          )}
        </PermissionGate>
      </main>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change subdomain?</DialogTitle>
            <DialogDescription>
              Changing your subdomain will update your public website URL. The
              old URL will no longer work. Continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmOpen(false)
                setPendingNewSubdomain(null)
              }}
              disabled={savingSubdomain}
            >
              Cancel
            </Button>
            <Button onClick={saveSubdomain} disabled={savingSubdomain}>
              {savingSubdomain ? "Configuring DNS…" : "Continue"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
